import React, { useState } from 'react'
import styled from 'styled-components'
import {
    useFloating,
    useInteractions,
    useClick,
    useDismiss,
    offset,
    flip,
    shift,
} from '@floating-ui/react'
import { Button } from '~common/components/button'
import { Icon } from '~common/components/icon'

export interface Attachment {
    id: string
    filename: string
    size?: number | null
}

export interface AttachmentDropdownProps {
    attachments: Attachment[]
    onDownload: (attachmentId: string) => void
    downloadingAttachmentId: string | null
    className?: string
}

/**
 * A dropdown component that displays multiple attachments using Floating UI.
 * Shows a single button with attachment count, and on click displays a dropdown
 * with all attachments that can be individually downloaded.
 */
export const AttachmentDropdown: React.FC<AttachmentDropdownProps> = ({
    attachments,
    onDownload,
    downloadingAttachmentId,
    className,
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(8), flip(), shift()],
    })

    const click = useClick(context)
    const dismiss = useDismiss(context)

    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
    ])

    const formatFileSize = (bytes?: number | null): string => {
        if (!bytes || bytes === null) return ''
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(1024))
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
    }

    const attachmentCount = attachments.length

    return (
        <Container className={className}>
            <DropdownButton
                ref={refs.setReference}
                {...getReferenceProps()}
                variant="secondary"
                size="small"
                icon="download"
                label={`${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`}
            />

            {isOpen && (
                <DropdownMenu
                    ref={refs.setFloating}
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    {attachments.map((attachment) => {
                        const isDownloading =
                            downloadingAttachmentId === attachment.id
                        return (
                            <DropdownItem
                                key={attachment.id}
                                onClick={() => {
                                    if (!isDownloading) {
                                        onDownload(attachment.id)
                                        setIsOpen(false)
                                    }
                                }}
                                disabled={isDownloading}
                            >
                                <AttachmentIcon>
                                    <Icon name="download" size="small" />
                                </AttachmentIcon>
                                <AttachmentInfo>
                                    <AttachmentName>
                                        {attachment.filename}
                                    </AttachmentName>
                                    {attachment.size && (
                                        <AttachmentSize>
                                            {formatFileSize(attachment.size)}
                                        </AttachmentSize>
                                    )}
                                </AttachmentInfo>
                                {isDownloading && (
                                    <DownloadingText>
                                        Downloading...
                                    </DownloadingText>
                                )}
                            </DropdownItem>
                        )
                    })}
                </DropdownMenu>
            )}
        </Container>
    )
}

// Styled Components
const Container = styled.div`
    position: relative;
    display: inline-block;
`

const DropdownButton = styled(Button)`
    white-space: nowrap;
`

const DropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    min-width: 250px;
    max-width: 400px;
    background: ${({ theme }) => theme.colors.background.bg1};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: ${({ theme }) => theme.spacing[1]};
    max-height: 300px;
    overflow-y: auto;
`

const DropdownItem = styled.button<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    padding: ${({ theme }) => theme.spacing[2]};
    border: none;
    background: none;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    border-radius: 4px;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.background.bg2};
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.success};
        outline-offset: -2px;
    }
`

const AttachmentIcon = styled.div`
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
`

const AttachmentInfo = styled.div`
    flex: 1;
    min-width: 0;
`

const AttachmentName = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
`

const AttachmentSize = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.text.tertiary};
    margin-top: 2px;
    text-align: left;
`

const DownloadingText = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.success};
    font-style: italic;
`
